import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import {
  ArrowLeft,
  Save,
  RotateCcw,
  Users,
  Palette,
  Building2,
  Sparkles,
  AlertTriangle,
  Trash2,
  Plus,
  UserMinus,
  Loader2,
  RefreshCw,
  Globe,
  Copy,
  Check,
  KeyRound,
  X,
  ClipboardList,
  CalendarDays,
  Info,
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Label } from '../../components/ui/Label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/Dialog'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
  AlertDialogTitle,
  AlertDialogDescription,
} from '../../components/ui/AlertDialog'
import { DialogHeaderWithIcon } from '../../components/ui/DialogHeaderWithIcon'
import { Avatar, AvatarFallback } from '../../components/ui/Avatar'
import { LogoDropzone } from '../../components/admin/LogoDropzone'
import { ORG_TYPE_OPTIONS } from '../../components/admin/OrgTypeSelector'
import { OrgBrandingPreview } from '../../components/admin/OrgBrandingPreview'
import { adminApi } from '../../services/api/admin'
import { brandingApi } from '../../services/api/branding'
import { demoTenantsApi } from '../../services/api/demoTenants'
import { usePermissionContext } from '../../contexts/PermissionContext'
import type { AdminOrgFull, OrgAccount, OrgMember, OrgType, Branding, OrgExchangeParticipant } from '../../types/branding'
import type { AdminOrgExchange } from '../../services/api/admin'
import { cn } from '../../lib/utils'
import { getDefaultBrandedUrl } from '../../lib/branding'
import toast from 'react-hot-toast'

const ENDPOINT_PENDING_MSG = 'Endpoint pending — see backlog issue #194'

const ORG_MEMBER_ROLE_LABELS: Record<string, string> = {
  org_owner: 'Owner',
  org_admin: 'Admin',
  org_staff: 'Staff',
}

const EXCHANGE_ROLE_LABELS: Record<string, string> = {
  qi_officer: 'QI Officer',
  exchanger: 'Exchanger',
  advisor: 'Advisor',
  title_company: 'Title Co',
  attorney: 'Attorney',
}

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  branch: 'Branch',
  division: 'Division',
  team: 'Team',
}

function initials(firstName: string | null, lastName: string | null, email: string): string {
  const first = firstName?.[0] ?? ''
  const last = lastName?.[0] ?? ''
  const combined = (first + last).toUpperCase()
  return combined || email[0].toUpperCase()
}

const inviteMemberSchema = z.object({
  email: z.string().email('Enter a valid email'),
  role: z.enum(['org_owner', 'org_admin', 'org_staff']),
})

type InviteMemberFormValues = z.infer<typeof inviteMemberSchema>

const addAccountSchema = z.object({
  name: z.string().min(1, 'Name is required').max(120, 'Name too long'),
  accountType: z.preprocess(
    (val) => (val === '' ? undefined : val),
    z.enum(['branch', 'division', 'team']).optional()
  ),
})

type AddAccountFormValues = z.infer<typeof addAccountSchema>

const deleteConfirmSchema = z.object({
  confirmation: z.string(),
})

type DeleteConfirmFormValues = z.infer<typeof deleteConfirmSchema>

const brandingSchema = z.object({
  name: z.string().min(1, 'Name is required').max(120, 'Name too long'),
  primaryColor: z.string().optional(),
  brandingAccentColor: z.string().optional(),
  website: z.string().optional(),
  logoFile: z.instanceof(File).optional(),
  logoDarkFile: z.instanceof(File).optional(),
})

type BrandingFormValues = z.infer<typeof brandingSchema>

const stateCodeRegex = /^[A-Z]{2}$/
const einRegex = /^\d{2}-\d{7}$|^\d{9}$/
const zipRegex = /^\d{5}(-\d{4})?$/

const orgInfoSchema = z.object({
  orgType: z.string().nullable().optional(),
  legalName: z.string().max(200).optional().or(z.literal('')),
  ein: z.string().regex(einRegex, 'Format: 12-3456789 or 9 digits').optional().or(z.literal('')),
  stateOfFormation: z.string().regex(stateCodeRegex, 'Enter 2-letter state code').optional().or(z.literal('')),
  addressStreet: z.string().max(200).optional().or(z.literal('')),
  addressCity: z.string().max(100).optional().or(z.literal('')),
  addressState: z.string().regex(stateCodeRegex, 'Enter 2-letter state code').optional().or(z.literal('')),
  addressZip: z.string().regex(zipRegex, 'Enter 5-digit ZIP').optional().or(z.literal('')),
  businessPhone: z.string().max(30).optional().or(z.literal('')),
  businessEmail: z.string().email('Enter a valid email').optional().or(z.literal('')),
  qiLicenseNumber: z.string().max(100).optional().or(z.literal('')),
  qiBondAmountDollars: z.string().optional().or(z.literal('')),
  riaCrdNumber: z.string().max(50).optional().or(z.literal('')),
  authorizedSignerName: z.string().max(200).optional().or(z.literal('')),
  authorizedSignerTitle: z.string().max(200).optional().or(z.literal('')),
})

type OrgInfoFormValues = z.infer<typeof orgInfoSchema>

interface OrgNameBarProps {
  orgName: string
  saving: boolean
  dirty: boolean
  onSave: () => void
  onDiscard: () => void
}

const OrgNameBar: React.FC<OrgNameBarProps> = ({
  orgName,
  saving,
  dirty,
  onSave,
  onDiscard,
}) => {
  const navigate = useNavigate()
  return (
    <div className="sticky top-0 z-30 bg-card/95 backdrop-blur border-b border-border px-6 py-3 flex items-center gap-3">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => navigate('/app/admin/orgs')}
        aria-label="Back to organizations"
        className="shrink-0"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      </Button>
      <h1 className="flex-1 text-lg font-semibold text-foreground truncate">
        {orgName}
      </h1>
      {dirty && (
        <Button
          variant="outline"
          size="sm"
          onClick={onDiscard}
          disabled={saving}
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
          Discard
        </Button>
      )}
      <Button
        size="sm"
        onClick={onSave}
        disabled={saving || !dirty}
      >
        {saving
          ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" aria-hidden="true" />
          : <Save className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
        }
        Save
      </Button>
    </div>
  )
}

interface ColorFieldProps {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
}

const ColorField: React.FC<ColorFieldProps> = ({
  id,
  label,
  value,
  onChange,
  placeholder,
}) => {
  const hexValue = value || ''
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`${id}-text`}>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          id={id}
          aria-label={`${label} color picker`}
          value={hexValue.startsWith('#') ? hexValue : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            'h-10 w-10 rounded-md border border-input cursor-pointer shrink-0',
            'bg-background p-0.5',
          )}
        />
        <Input
          id={`${id}-text`}
          aria-label={label}
          placeholder={placeholder ?? '#000000'}
          value={hexValue}
          onChange={(e) => onChange(e.target.value)}
          className="font-mono flex-1"
        />
      </div>
    </div>
  )
}

interface LogoPreviewCardProps {
  lightLogoUrl: string | null | undefined
  darkLogoUrl: string | null | undefined
  onDeleteLight?: () => Promise<void>
  onDeleteDark?: () => Promise<void>
}

const LogoPreviewCard: React.FC<LogoPreviewCardProps> = ({
  lightLogoUrl,
  darkLogoUrl,
  onDeleteLight,
  onDeleteDark,
}) => {
  const [deletingLight, setDeletingLight] = useState(false)
  const [deletingDark, setDeletingDark] = useState(false)
  if (!lightLogoUrl && !darkLogoUrl) {
    return (
      <p className="text-sm text-muted-foreground py-1">
        No logo uploaded yet — drop one below
      </p>
    )
  }
  const handleDeleteLight = async () => {
    if (!onDeleteLight) return
    setDeletingLight(true)
    try {
      await onDeleteLight()
    } finally {
      setDeletingLight(false)
    }
  }
  const handleDeleteDark = async () => {
    if (!onDeleteDark) return
    setDeletingDark(true)
    try {
      await onDeleteDark()
    } finally {
      setDeletingDark(false)
    }
  }
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex gap-4">
        <div className="flex-1 space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Light</p>
          <div className="relative group rounded-lg border border-border bg-white flex items-center justify-center h-20 overflow-hidden">
            {lightLogoUrl ? (
              <>
                <img
                  src={lightLogoUrl}
                  alt="Light logo preview"
                  className="max-h-14 max-w-full object-contain"
                />
                <button
                  type="button"
                  aria-label="Delete light logo"
                  disabled={deletingLight}
                  onClick={handleDeleteLight}
                  className={cn(
                    'absolute top-1 right-1 h-6 w-6 rounded-full bg-background/80 border border-border',
                    'flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity',
                    'hover:bg-destructive hover:text-destructive-foreground focus-visible:opacity-100',
                  )}
                >
                  {deletingLight
                    ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                    : <X className="h-3 w-3" aria-hidden="true" />
                  }
                </button>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">None</p>
            )}
          </div>
        </div>
        <div className="flex-1 space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Dark</p>
          <div className="relative group rounded-lg border border-border bg-zinc-900 flex items-center justify-center h-20 overflow-hidden">
            {darkLogoUrl ? (
              <>
                <img
                  src={darkLogoUrl}
                  alt="Dark logo preview"
                  className="max-h-14 max-w-full object-contain"
                />
                <button
                  type="button"
                  aria-label="Delete dark logo"
                  disabled={deletingDark}
                  onClick={handleDeleteDark}
                  className={cn(
                    'absolute top-1 right-1 h-6 w-6 rounded-full bg-background/80 border border-border',
                    'flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity',
                    'hover:bg-destructive hover:text-destructive-foreground focus-visible:opacity-100',
                  )}
                >
                  {deletingDark
                    ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                    : <X className="h-3 w-3" aria-hidden="true" />
                  }
                </button>
              </>
            ) : (
              <p className="text-xs text-muted-foreground text-center px-2">None</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

interface BrandingTabProps {
  org: AdminOrgFull
  orgId: string
  onDirtyChange: (dirty: boolean) => void
  submitRef: React.MutableRefObject<(() => Promise<void>) | null>
  onDeleteLogo?: () => Promise<void>
  onDeleteLogoDark?: () => Promise<void>
}

const BrandingTab: React.FC<BrandingTabProps> = ({
  org,
  orgId,
  onDirtyChange,
  submitRef,
  onDeleteLogo,
  onDeleteLogoDark,
}) => {
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isDirty },
  } = useForm<BrandingFormValues>({
    resolver: zodResolver(brandingSchema),
    defaultValues: {
      name: org.name,
      primaryColor: org.primaryColor ?? '',
      brandingAccentColor: org.brandingAccentColor ?? '',
      website: org.website ?? '',
      logoFile: undefined,
      logoDarkFile: undefined,
    },
  })

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  const watchedName = watch('name')
  const watchedPrimary = watch('primaryColor')
  const watchedLogoFile = watch('logoFile')
  const watchedLogoDarkFile = watch('logoDarkFile')

  const hasLightLogo = !!(org.logoUrl || watchedLogoFile)
  const hasDarkLogo = !!(org.brandingLogoDarkUrl || watchedLogoDarkFile)
  const singleLogoWarning = (hasLightLogo || hasDarkLogo) && !(hasLightLogo && hasDarkLogo)

  const defaultUrl = getDefaultBrandedUrl(org.slug)

  const handleCopyUrl = useCallback(() => {
    navigator.clipboard.writeText(defaultUrl)
    toast.success('Copied')
  }, [defaultUrl])

  const onSubmit = useCallback(async (values: BrandingFormValues) => {
    try {
      await adminApi.patchOrg(orgId, {
        name: values.name,
        website: values.website,
      })
      const normalizeHex = (v: string): string | null => {
        const trimmed = v.trim()
        if (!trimmed) return null
        return trimmed.startsWith('#') ? trimmed : `#${trimmed}`
      }
      const brandingPatch: Record<string, string | null> = {}
      if (values.primaryColor !== (org.primaryColor ?? '')) {
        brandingPatch.primaryColor = normalizeHex(values.primaryColor ?? '')
      }
      if (Object.keys(brandingPatch).length > 0) {
        await brandingApi.update(orgId, brandingPatch as Partial<Branding>)
      }
      if (values.logoFile) {
        await brandingApi.uploadLogo(orgId, values.logoFile)
      }
      if (values.logoDarkFile) {
        await brandingApi.uploadLogoDark(orgId, values.logoDarkFile)
      }
      queryClient.invalidateQueries({ queryKey: ['admin-org', orgId] })
      queryClient.invalidateQueries({ queryKey: ['admin-orgs'] })
      queryClient.invalidateQueries({ queryKey: ['org-branding', orgId] })
      toast.success('Branding saved')
    } catch {
      toast.error('Failed to save branding')
    }
  }, [orgId, org.primaryColor, queryClient])

  submitRef.current = handleSubmit(onSubmit) as unknown as () => Promise<void>

  return (
    <div className="py-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="max-w-xl space-y-8">
          <section className="space-y-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <h2 className="text-sm font-semibold text-foreground">Identity</h2>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="branding-name">Organization name</Label>
              <Input
                id="branding-name"
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'branding-name-error' : undefined}
                {...register('name')}
              />
              {errors.name && (
                <p id="branding-name-error" role="alert" className="text-xs text-destructive">
                  {errors.name.message as string}
                </p>
              )}
            </div>

          </section>

          <section className="space-y-4">
            <div className="flex items-center gap-2">
              <Palette className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <h2 className="text-sm font-semibold text-foreground">Branding</h2>
            </div>

            <LogoPreviewCard
              lightLogoUrl={org.logoUrl}
              darkLogoUrl={org.brandingLogoDarkUrl}
              onDeleteLight={onDeleteLogo}
              onDeleteDark={onDeleteLogoDark}
            />

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Logo (light)</Label>
                <Controller
                  name="logoFile"
                  control={control}
                  render={({ field }) => (
                    <LogoDropzone
                      value={field.value}
                      onChange={field.onChange}
                      existingLogoUrl={org.logoUrl ?? undefined}
                      onDelete={onDeleteLogo}
                    />
                  )}
                />
              </div>

              <div className="space-y-1.5">
                <Label>Logo (dark mode)</Label>
                <Controller
                  name="logoDarkFile"
                  control={control}
                  render={({ field }) => (
                    <LogoDropzone
                      value={field.value}
                      onChange={field.onChange}
                      existingLogoUrl={org.brandingLogoDarkUrl ?? undefined}
                      onDelete={onDeleteLogoDark}
                    />
                  )}
                />
              </div>

              {singleLogoWarning && (
                <p className="text-xs text-muted-foreground">
                  Only one logo uploaded. The same image will be used for both light and dark mode.
                  Upload a separate dark-mode logo if your light logo doesn't read well on dark backgrounds.
                </p>
              )}
            </div>

            <Controller
              name="primaryColor"
              control={control}
              render={({ field }) => (
                <ColorField
                  id="branding-primary"
                  label="Primary color"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  placeholder="#1a2b3c"
                />
              )}
            />
          </section>

          <section className="space-y-4">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <h2 className="text-sm font-semibold text-foreground">Domain</h2>
            </div>

            <div className="space-y-1.5">
              <Label>Default sign-in URL</Label>
              <div className="flex items-center gap-2 rounded-md border border-border bg-muted px-3 py-2">
                <code className="flex-1 text-sm text-muted-foreground font-mono">
                  {defaultUrl}
                </code>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Copy sign-in URL"
                  className="h-7 w-7 shrink-0"
                  onClick={handleCopyUrl}
                >
                  <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Customers can sign in here today. Set a custom domain below if you want to use your own.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="branding-website">Custom domain / website</Label>
              <Input
                id="branding-website"
                placeholder="https://exchange.acmetitle.com"
                {...register('website')}
              />
            </div>
          </section>
        </div>

        <div className="lg:sticky lg:top-20 lg:self-start">
          <OrgBrandingPreview
            orgName={watchedName}
            primaryColor={watchedPrimary}
            logoUrl={org.logoUrl}
            logoDarkUrl={org.brandingLogoDarkUrl}
            previewLogoFile={watchedLogoFile}
            previewLogoDarkFile={watchedLogoDarkFile}
          />
        </div>
      </div>
    </div>
  )
}

interface OrgInfoTabProps {
  org: AdminOrgFull
  orgId: string
  onDirtyChange: (dirty: boolean) => void
  submitRef: React.MutableRefObject<(() => Promise<void>) | null>
}

const OrgInfoTab: React.FC<OrgInfoTabProps> = ({
  org,
  orgId,
  onDirtyChange,
  submitRef,
}) => {
  const queryClient = useQueryClient()

  const toUpperTransform = (value: string) => value.toUpperCase()

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty },
  } = useForm<OrgInfoFormValues>({
    resolver: zodResolver(orgInfoSchema),
    defaultValues: {
      orgType: org.orgType ?? null,
      legalName: org.legalName ?? '',
      ein: org.ein ?? '',
      stateOfFormation: org.stateOfFormation ?? '',
      addressStreet: org.addressStreet ?? '',
      addressCity: org.addressCity ?? '',
      addressState: org.addressState ?? '',
      addressZip: org.addressZip ?? '',
      businessPhone: org.businessPhone ?? '',
      businessEmail: org.businessEmail ?? '',
      qiLicenseNumber: org.qiLicenseNumber ?? '',
      qiBondAmountDollars: org.qiBondAmountCents != null
        ? String(org.qiBondAmountCents / 100)
        : '',
      riaCrdNumber: org.riaCrdNumber ?? '',
      authorizedSignerName: org.authorizedSignerName ?? '',
      authorizedSignerTitle: org.authorizedSignerTitle ?? '',
    },
  })

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  const onSubmit = useCallback(async (values: OrgInfoFormValues) => {
    const nullOrStr = (v: string | undefined): string | null =>
      v && v.trim() !== '' ? v.trim() : null
    const bondDollars = values.qiBondAmountDollars && values.qiBondAmountDollars.trim() !== ''
      ? parseFloat(values.qiBondAmountDollars)
      : null
    const bondCents = bondDollars != null && !isNaN(bondDollars)
      ? Math.round(bondDollars * 100)
      : null
    try {
      await adminApi.patchOrg(orgId, {
        orgType: (values.orgType as OrgType | undefined) ?? undefined,
        legalName: nullOrStr(values.legalName),
        ein: nullOrStr(values.ein),
        stateOfFormation: nullOrStr(values.stateOfFormation),
        addressStreet: nullOrStr(values.addressStreet),
        addressCity: nullOrStr(values.addressCity),
        addressState: nullOrStr(values.addressState),
        addressZip: nullOrStr(values.addressZip),
        businessPhone: nullOrStr(values.businessPhone),
        businessEmail: nullOrStr(values.businessEmail),
        qiLicenseNumber: nullOrStr(values.qiLicenseNumber),
        qiBondAmountCents: bondCents,
        riaCrdNumber: nullOrStr(values.riaCrdNumber),
        authorizedSignerName: nullOrStr(values.authorizedSignerName),
        authorizedSignerTitle: nullOrStr(values.authorizedSignerTitle),
      })
      queryClient.invalidateQueries({ queryKey: ['admin-org', orgId] })
      queryClient.invalidateQueries({ queryKey: ['admin-orgs'] })
      toast.success('Org info saved')
    } catch {
      toast.error('Failed to save org info')
    }
  }, [orgId, queryClient])

  submitRef.current = handleSubmit(onSubmit) as unknown as () => Promise<void>

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="py-6 max-w-xl space-y-8"
    >
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Info className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-foreground">Type</h2>
        </div>
        <Controller
          name="orgType"
          control={control}
          render={({ field }) => (
            <div
              role="radiogroup"
              aria-label="Organization type"
              className="grid grid-cols-1 md:grid-cols-2 gap-3"
            >
              {ORG_TYPE_OPTIONS.map((option) => {
                const selected = field.value === option.value
                const IconComponent = option.icon
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={option.label}
                    onClick={() => field.onChange(option.value)}
                    className={cn(
                      'flex items-start gap-3 rounded-lg border-2 p-4 text-left transition-all',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      selected
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border hover:border-primary/40 hover:bg-muted/40',
                    )}
                  >
                    <IconComponent
                      className={cn(
                        'h-5 w-5 mt-0.5 shrink-0',
                        selected ? 'text-primary' : 'text-muted-foreground',
                      )}
                      aria-hidden="true"
                    />
                    <div className="min-w-0">
                      <p className={cn(
                        'text-sm font-semibold leading-tight',
                        selected ? 'text-primary' : 'text-foreground',
                      )}>
                        {option.label}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                        {option.description}
                      </p>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        />
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-foreground">Identity</h2>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="orginfo-legal-name">Legal Name</Label>
          <Input
            id="orginfo-legal-name"
            aria-invalid={!!errors.legalName}
            {...register('legalName')}
          />
          {errors.legalName && (
            <p role="alert" className="text-xs text-destructive">{errors.legalName.message as string}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="orginfo-ein">EIN</Label>
          <Input
            id="orginfo-ein"
            placeholder="12-3456789"
            aria-invalid={!!errors.ein}
            aria-describedby={errors.ein ? 'orginfo-ein-error' : 'orginfo-ein-hint'}
            {...register('ein')}
          />
          {errors.ein
            ? <p id="orginfo-ein-error" role="alert" className="text-xs text-destructive">{errors.ein.message as string}</p>
            : <p id="orginfo-ein-hint" className="text-xs text-muted-foreground">Format: 12-3456789 or 9 digits</p>
          }
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="orginfo-state-of-formation">State of Formation</Label>
          <Input
            id="orginfo-state-of-formation"
            placeholder="DE"
            maxLength={2}
            aria-invalid={!!errors.stateOfFormation}
            {...register('stateOfFormation', { setValueAs: toUpperTransform })}
          />
          {errors.stateOfFormation && (
            <p role="alert" className="text-xs text-destructive">{errors.stateOfFormation.message as string}</p>
          )}
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-foreground">Contact</h2>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="orginfo-address-street">Address Street</Label>
          <Input
            id="orginfo-address-street"
            aria-invalid={!!errors.addressStreet}
            {...register('addressStreet')}
          />
          {errors.addressStreet && (
            <p role="alert" className="text-xs text-destructive">{errors.addressStreet.message as string}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="orginfo-address-city">City</Label>
          <Input
            id="orginfo-address-city"
            aria-invalid={!!errors.addressCity}
            {...register('addressCity')}
          />
          {errors.addressCity && (
            <p role="alert" className="text-xs text-destructive">{errors.addressCity.message as string}</p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="orginfo-address-state">State</Label>
            <Input
              id="orginfo-address-state"
              placeholder="CA"
              maxLength={2}
              aria-invalid={!!errors.addressState}
              {...register('addressState', { setValueAs: toUpperTransform })}
            />
            {errors.addressState && (
              <p role="alert" className="text-xs text-destructive">{errors.addressState.message as string}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="orginfo-address-zip">ZIP</Label>
            <Input
              id="orginfo-address-zip"
              placeholder="90210"
              aria-invalid={!!errors.addressZip}
              {...register('addressZip')}
            />
            {errors.addressZip && (
              <p role="alert" className="text-xs text-destructive">{errors.addressZip.message as string}</p>
            )}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="orginfo-business-phone">Business Phone</Label>
          <Input
            id="orginfo-business-phone"
            type="tel"
            aria-invalid={!!errors.businessPhone}
            {...register('businessPhone')}
          />
          {errors.businessPhone && (
            <p role="alert" className="text-xs text-destructive">{errors.businessPhone.message as string}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="orginfo-business-email">Business Email</Label>
          <Input
            id="orginfo-business-email"
            type="email"
            aria-invalid={!!errors.businessEmail}
            {...register('businessEmail')}
          />
          {errors.businessEmail && (
            <p role="alert" className="text-xs text-destructive">{errors.businessEmail.message as string}</p>
          )}
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-foreground">Compliance</h2>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="orginfo-qi-license">QI License Number</Label>
          <Input
            id="orginfo-qi-license"
            aria-invalid={!!errors.qiLicenseNumber}
            {...register('qiLicenseNumber')}
          />
          {errors.qiLicenseNumber && (
            <p role="alert" className="text-xs text-destructive">{errors.qiLicenseNumber.message as string}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="orginfo-qi-bond">QI Bond Amount (USD)</Label>
          <Input
            id="orginfo-qi-bond"
            type="number"
            min={0}
            step={1}
            placeholder="1000000"
            aria-invalid={!!errors.qiBondAmountDollars}
            {...register('qiBondAmountDollars')}
          />
          {errors.qiBondAmountDollars && (
            <p role="alert" className="text-xs text-destructive">{errors.qiBondAmountDollars.message as string}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="orginfo-ria-crd">RIA CRD Number</Label>
          <Input
            id="orginfo-ria-crd"
            aria-invalid={!!errors.riaCrdNumber}
            {...register('riaCrdNumber')}
          />
          {errors.riaCrdNumber && (
            <p role="alert" className="text-xs text-destructive">{errors.riaCrdNumber.message as string}</p>
          )}
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-foreground">Default Signer</h2>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="orginfo-signer-name">Authorized Signer Name</Label>
          <Input
            id="orginfo-signer-name"
            aria-invalid={!!errors.authorizedSignerName}
            {...register('authorizedSignerName')}
          />
          {errors.authorizedSignerName && (
            <p role="alert" className="text-xs text-destructive">{errors.authorizedSignerName.message as string}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="orginfo-signer-title">Authorized Signer Title</Label>
          <Input
            id="orginfo-signer-title"
            placeholder="Director of Operations"
            aria-invalid={!!errors.authorizedSignerTitle}
            {...register('authorizedSignerTitle')}
          />
          {errors.authorizedSignerTitle && (
            <p role="alert" className="text-xs text-destructive">{errors.authorizedSignerTitle.message as string}</p>
          )}
        </div>
      </section>

      <Button
        type="submit"
        size="sm"
        disabled={!isDirty}
      >
        <Save className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
        Save Changes
      </Button>
    </form>
  )
}

interface MembersTabProps {
  orgId: string
}

const MembersTab: React.FC<MembersTabProps> = ({ orgId }) => {
  const queryClient = useQueryClient()
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [removingId, setRemovingId] = useState<string | null>(null)

  const { data: membersData, isLoading: membersLoading, isError: membersError } = useQuery({
    queryKey: ['admin-org-members', orgId],
    queryFn: () => adminApi.getOrgMembers(orgId),
    staleTime: 30_000,
  })

  const { data: participantsData, isLoading: participantsLoading } = useQuery({
    queryKey: ['admin-org-exchange-participants', orgId],
    queryFn: () => adminApi.getOrgExchangeParticipants(orgId),
    staleTime: 30_000,
  })

  const members: OrgMember[] = membersData ?? []
  const participants: OrgExchangeParticipant[] = participantsData ?? []

  const participantsByExchange = participants.reduce<Map<string, { name: string | null; rows: OrgExchangeParticipant[] }>>(
    (acc, p) => {
      const existing = acc.get(p.exchangeId)
      if (existing) {
        existing.rows.push(p)
      } else {
        acc.set(p.exchangeId, { name: p.exchangeName, rows: [p] })
      }
      return acc
    },
    new Map()
  )

  const {
    register,
    handleSubmit,
    reset: resetInvite,
    formState: { errors },
  } = useForm<InviteMemberFormValues>({
    resolver: zodResolver(inviteMemberSchema),
    defaultValues: { email: '', role: 'org_staff' },
  })

  const onInvite = useCallback(async (values: InviteMemberFormValues) => {
    setInviting(true)
    try {
      await adminApi.inviteOrgMember(orgId, values)
      queryClient.invalidateQueries({ queryKey: ['admin-org-members', orgId] })
      toast.success(`Invite sent to ${values.email}`)
      resetInvite()
      setInviteOpen(false)
    } catch {
      toast.error(ENDPOINT_PENDING_MSG)
    } finally {
      setInviting(false)
    }
  }, [orgId, queryClient, resetInvite])

  const handleRemove = useCallback(async (membershipId: string) => {
    setRemovingId(membershipId)
    try {
      await adminApi.deleteOrgMembership(membershipId)
      queryClient.invalidateQueries({ queryKey: ['admin-org-members', orgId] })
      toast.success('Member removed')
    } catch {
      toast.error(ENDPOINT_PENDING_MSG)
    } finally {
      setRemovingId(null)
    }
  }, [orgId, queryClient])

  if (membersLoading || participantsLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
      </div>
    )
  }

  if (membersError) {
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-4 my-4">
        <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <p className="text-sm font-medium">
            Members endpoint pending — see backlog issue #194
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="py-4 space-y-6">
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <span className="text-sm font-semibold text-foreground">Organization Members</span>
            <span className="text-xs text-muted-foreground">
              {members.length} member{members.length !== 1 ? 's' : ''}
            </span>
          </div>
          <Button
            size="sm"
            onClick={() => setInviteOpen(true)}
          >
            <Plus className="h-4 w-4 mr-1.5" aria-hidden="true" />
            Invite member
          </Button>
        </div>
        {members.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            No members yet — invite your first
          </div>
        ) : (
          <div className="divide-y divide-border">
            {members.map((member) => {
              const profile = member.profile
              const displayName = profile
                ? `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim() || profile.email
                : '—'
              const avatarText = profile
                ? initials(profile.firstName, profile.lastName, profile.email)
                : '?'
              return (
                <div
                  key={member.id}
                  className="flex items-center gap-3 px-4 py-3"
                >
                  <Avatar className="h-10 w-10 shrink-0">
                    <AvatarFallback className="text-xs bg-muted text-muted-foreground">{avatarText}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                    <p className="text-xs text-muted-foreground truncate">{profile?.email}</p>
                  </div>
                  <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-secondary text-secondary-foreground">
                    {ORG_MEMBER_ROLE_LABELS[member.role] ?? member.role}
                  </span>
                  <span className={cn(
                    'h-2 w-2 rounded-full shrink-0',
                    member.active ? 'bg-emerald-500' : 'bg-muted-foreground/40',
                  )} aria-label={member.active ? 'Active' : 'Inactive'} />
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove ${displayName}`}
                    disabled={removingId === member.id}
                    onClick={() => handleRemove(member.id)}
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  >
                    {removingId === member.id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                      : <UserMinus className="h-3.5 w-3.5" aria-hidden="true" />
                    }
                  </Button>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {participantsByExchange.size > 0 && (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
            <Sparkles className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <span className="text-sm font-semibold text-foreground">Exchange Participants</span>
            <span className="text-xs text-muted-foreground">
              Active in {participantsByExchange.size} exchange{participantsByExchange.size !== 1 ? 's' : ''}
            </span>
          </div>
          <div>
            {Array.from(participantsByExchange.entries()).map(([exchangeId, { name, rows }]) => (
              <div key={exchangeId}>
                <div className="px-4 py-2 bg-muted/40 border-b border-border">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {name ?? exchangeId} — {rows.length} participant{rows.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="divide-y divide-border">
                  {rows.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-muted-foreground">No participants yet</p>
                  ) : rows.map((p) => {
                    const displayName = p.firstName || p.lastName
                      ? `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim()
                      : p.email ?? '—'
                    const avatarText = initials(p.firstName, p.lastName, p.email ?? '?')
                    return (
                      <div
                        key={p.id}
                        className="flex items-center gap-3 px-4 py-3"
                      >
                        <Avatar className="h-10 w-10 shrink-0">
                          <AvatarFallback className="text-xs bg-muted text-muted-foreground">{avatarText}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                          <p className="text-xs text-muted-foreground truncate">{p.email}</p>
                        </div>
                        <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-accent/30 text-accent-foreground">
                          {EXCHANGE_ROLE_LABELS[p.exchangeRole] ?? p.exchangeRole}
                        </span>
                        <span className={cn(
                          'h-2 w-2 rounded-full shrink-0',
                          p.active ? 'bg-emerald-500' : 'bg-muted-foreground/40',
                        )} aria-label={p.active ? 'Active' : 'Inactive'} />
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog
        open={inviteOpen}
        onOpenChange={(open) => {
          if (!open) resetInvite()
          setInviteOpen(open)
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invite member</DialogTitle>
            <DialogDescription>
              Send an invitation to join this organization.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onInvite)} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="colleague@company.com"
                aria-invalid={!!errors.email}
                {...register('email')}
              />
              {errors.email && (
                <p role="alert" className="text-xs text-destructive">{errors.email.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="invite-role">Role</Label>
              <select
                id="invite-role"
                className={cn(
                  'flex h-10 w-full rounded-md border border-input bg-background',
                  'px-3 py-2 text-sm ring-offset-background',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                )}
                {...register('role')}
              >
                <option value="org_staff">Staff</option>
                <option value="org_admin">Admin</option>
                <option value="org_owner">Owner</option>
              </select>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  resetInvite()
                  setInviteOpen(false)
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={inviting}
              >
                {inviting ? 'Sending…' : 'Send invite'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

interface AccountsTabProps {
  orgId: string
}

const AccountsTab: React.FC<AccountsTabProps> = ({ orgId }) => {
  const queryClient = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [adding, setAdding] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-org-accounts', orgId],
    queryFn: () => adminApi.getOrgAccounts(orgId),
    staleTime: 30_000,
  })

  const accounts: OrgAccount[] = data ?? []

  const {
    register,
    handleSubmit,
    reset: resetAdd,
    formState: { errors },
  } = useForm<AddAccountFormValues>({
    resolver: zodResolver(addAccountSchema),
    defaultValues: { name: '', accountType: undefined },
  })

  const onAdd = useCallback(async (values: AddAccountFormValues) => {
    setAdding(true)
    try {
      await adminApi.createOrgAccount(orgId, {
        name: values.name,
        accountType: values.accountType,
      })
      queryClient.invalidateQueries({ queryKey: ['admin-org-accounts', orgId] })
      toast.success(`Account "${values.name}" created`)
      resetAdd()
      setAddOpen(false)
    } catch {
      toast.error(ENDPOINT_PENDING_MSG)
    } finally {
      setAdding(false)
    }
  }, [orgId, queryClient, resetAdd])

  const handleDelete = useCallback(async (accountId: string, name: string) => {
    setDeletingId(accountId)
    try {
      await adminApi.deleteOrgAccount(orgId, accountId)
      queryClient.invalidateQueries({ queryKey: ['admin-org-accounts', orgId] })
      toast.success(`"${name}" removed`)
    } catch {
      toast.error(ENDPOINT_PENDING_MSG)
    } finally {
      setDeletingId(null)
    }
  }, [orgId, queryClient])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-4 my-4">
        <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <p className="text-sm font-medium">
            Accounts endpoint pending — see backlog issue #194
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="py-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-foreground">
            Sub-divisions ({accounts.length})
          </p>
          <p className="text-xs text-muted-foreground">
            Branches, divisions, or teams within this organization.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setAddOpen(true)}
        >
          <Plus className="h-4 w-4 mr-1.5" aria-hidden="true" />
          Add account
        </Button>
      </div>
      {accounts.length === 0 ? (
        <div className="text-center py-8 text-sm text-muted-foreground">
          No accounts yet. Add a branch, division, or team.
        </div>
      ) : (
        <div className="divide-y divide-border rounded-lg border border-border overflow-hidden">
          {accounts.map((account) => (
            <div
              key={account.id}
              className="flex items-center gap-3 px-4 py-3 bg-card"
            >
              <Building2 className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{account.name}</p>
                {account.accountType && (
                  <p className="text-xs text-muted-foreground capitalize">
                    {ACCOUNT_TYPE_LABELS[account.accountType] ?? account.accountType}
                  </p>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Remove account ${account.name}`}
                disabled={deletingId === account.id}
                onClick={() => handleDelete(account.id, account.name)}
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
              >
                {deletingId === account.id
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  : <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                }
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog
        open={addOpen}
        onOpenChange={(open) => {
          if (!open) resetAdd()
          setAddOpen(open)
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add account</DialogTitle>
            <DialogDescription>
              Create a branch, division, or team within this organization.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onAdd)} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="account-name">Name</Label>
              <Input
                id="account-name"
                placeholder="West Coast Branch"
                aria-invalid={!!errors.name}
                {...register('name')}
              />
              {errors.name && (
                <p role="alert" className="text-xs text-destructive">{errors.name.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="account-type">Type</Label>
              <select
                id="account-type"
                className={cn(
                  'flex h-10 w-full rounded-md border border-input bg-background',
                  'px-3 py-2 text-sm ring-offset-background',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                )}
                {...register('accountType')}
              >
                <option value="">— select type —</option>
                <option value="branch">Branch</option>
                <option value="division">Division</option>
                <option value="team">Team</option>
              </select>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  resetAdd()
                  setAddOpen(false)
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={adding}
              >
                {adding ? 'Adding…' : 'Create account'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

interface CredentialsBannerProps {
  credentials: { email: string; password: string }[]
  copiedRows: Set<string>
  onCopyRow: (email: string) => void
  onDismiss: () => void
}

const CredentialsBanner: React.FC<CredentialsBannerProps> = ({
  credentials,
  copiedRows,
  onCopyRow,
  onDismiss,
}) => {
  const allCopied = credentials.length > 0 && credentials.every((c) => copiedRows.has(c.email))
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-4 space-y-2">
      <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <p className="text-xs font-semibold">
          Save these credentials — they will not be shown again.
        </p>
      </div>
      {credentials.map((cred) => {
        const copied = copiedRows.has(cred.email)
        return (
          <div
            key={cred.email}
            className="flex items-center gap-2 py-1.5 border-t border-amber-200 dark:border-amber-800 font-mono text-xs"
          >
            <span className="text-muted-foreground flex-1 truncate">{cred.email}</span>
            <span className="text-foreground font-semibold">{cred.password}</span>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Copy credentials for ${cred.email}`}
              className="h-6 w-6 shrink-0"
              onClick={() => {
                navigator.clipboard.writeText(`${cred.email} ${cred.password}`)
                onCopyRow(cred.email)
              }}
            >
              {copied
                ? <Check className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
                : <Copy className="h-3.5 w-3.5" aria-hidden="true" />
              }
            </Button>
          </div>
        )
      })}
      <Button
        variant="ghost"
        size="sm"
        onClick={onDismiss}
        disabled={!allCopied}
        className="w-full text-muted-foreground disabled:opacity-40"
        aria-label="Dismiss credentials banner"
      >
        Dismiss
      </Button>
    </div>
  )
}

const EXCHANGE_STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  pending: 'Pending',
  closed: 'Closed',
  cancelled: 'Cancelled',
  identified: 'Identified',
  completed: 'Completed',
}

interface ResetExchangeDemoSectionProps {
  orgId: string
}

const ResetExchangeDemoSection: React.FC<ResetExchangeDemoSectionProps> = ({ orgId }) => {
  const queryClient = useQueryClient()
  const [confirmExchangeId, setConfirmExchangeId] = useState<string | null>(null)
  const [resetting, setResetting] = useState(false)

  const { data: exchanges, isLoading } = useQuery({
    queryKey: ['admin-org-exchanges', orgId],
    queryFn: () => adminApi.getOrgExchanges(orgId),
    staleTime: 30_000,
  })

  const exchangeList: AdminOrgExchange[] = exchanges ?? []

  const handleConfirmReset = useCallback(async () => {
    if (!confirmExchangeId) return
    setResetting(true)
    try {
      await adminApi.resetExchangeDemoState(confirmExchangeId)
      queryClient.invalidateQueries({ queryKey: ['admin-org-exchanges', orgId] })
      queryClient.invalidateQueries({ queryKey: ['admin-org', orgId] })
      toast.success('Exchange demo state reset')
      setConfirmExchangeId(null)
    } catch {
      toast.error('Failed to reset exchange demo state')
    } finally {
      setResetting(false)
    }
  }, [confirmExchangeId, orgId, queryClient])

  const confirmingExchange = exchangeList.find((e) => e.id === confirmExchangeId) ?? null

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-3">
      <div className="space-y-0.5">
        <p className="text-sm font-semibold text-foreground">Reset Exchange Demo State</p>
        <p className="text-xs text-muted-foreground">
          Wipes hub progress, KYC status, sensitive profile data, and soft-deletes documents for an exchange.
          Useful between demo runs.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 py-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
          <span className="text-xs text-muted-foreground">Loading exchanges…</span>
        </div>
      ) : exchangeList.length === 0 ? (
        <p className="text-xs text-muted-foreground py-1">No exchanges found for this organization.</p>
      ) : (
        <div className="divide-y divide-border rounded-md border border-border overflow-hidden">
          {exchangeList.map((exchange) => (
            <div
              key={exchange.id}
              className="flex items-center gap-3 px-3 py-2.5 bg-background"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{exchange.name}</p>
              </div>
              <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-secondary text-secondary-foreground shrink-0">
                {EXCHANGE_STATUS_LABELS[exchange.status] ?? exchange.status}
              </span>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setConfirmExchangeId(exchange.id)}
                aria-label={`Reset demo state for ${exchange.name}`}
                className="shrink-0"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
                Reset
              </Button>
            </div>
          ))}
        </div>
      )}

      <AlertDialog
        open={!!confirmExchangeId}
        onOpenChange={(open) => {
          if (!open) setConfirmExchangeId(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset demo state?</AlertDialogTitle>
            <AlertDialogDescription>
              This will wipe hub progress and KYC for all participants on
              {confirmingExchange ? ` "${confirmingExchange.name}"` : ' this exchange'}.
              Sensitive profile data will be cleared and documents will be soft-deleted.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setConfirmExchangeId(null)}
              disabled={resetting}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleConfirmReset}
              disabled={resetting}
            >
              {resetting ? 'Resetting…' : 'Reset demo state'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

type PersonaKey =
  | 'org_admin'
  | 'exchanger'
  | 'advisor'
  | 'qi_officer'
  | 'title_company'
  | 'attorney'

interface PersonaOption {
  key: PersonaKey
  label: string
  defaultName: string
  email: string
}

const PERSONA_OPTIONS: PersonaOption[] = [
  { key: 'org_admin', label: 'Org Admin', defaultName: 'Org Admin', email: 'org.admin@open1031.demo' },
  { key: 'exchanger', label: 'Exchanger', defaultName: 'Demo Exchanger', email: 'exchanger@open1031.demo' },
  { key: 'qi_officer', label: 'QI Officer', defaultName: 'QI Officer', email: 'qi.officer@open1031.demo' },
  { key: 'advisor', label: 'Advisor', defaultName: 'Demo Advisor', email: 'advisor@open1031.demo' },
  { key: 'title_company', label: 'Title Company', defaultName: 'Title Company', email: 'title@open1031.demo' },
  { key: 'attorney', label: 'Attorney', defaultName: 'Demo Attorney', email: 'attorney@open1031.demo' },
]

const PERSONA_BY_KEY: Record<PersonaKey, PersonaOption> = PERSONA_OPTIONS.reduce(
  (acc, option) => ({ ...acc, [option.key]: option }),
  {} as Record<PersonaKey, PersonaOption>,
)

const MINIMAL_ROSTER_KEYS: PersonaKey[] = ['org_admin', 'exchanger']

interface RosterMember {
  id: string
  name: string
  email: string
  personaKey: PersonaKey
}

let rosterIdCounter = 0
function nextRosterId(): string {
  rosterIdCounter += 1
  return `roster-${rosterIdCounter}`
}

function buildRosterFromKeys(keys: PersonaKey[]): RosterMember[] {
  return keys.map((key) => {
    const option = PERSONA_BY_KEY[key]
    return {
      id: nextRosterId(),
      name: option.defaultName,
      email: option.email,
      personaKey: key,
    }
  })
}

interface DemoSeedTabProps {
  org: AdminOrgFull
  orgId: string
}

const DemoSeedTab: React.FC<DemoSeedTabProps> = ({ org, orgId }) => {
  const queryClient = useQueryClient()
  const { isPlatformAdmin } = usePermissionContext()
  const [seeding, setSeeding] = useState(false)
  const [toggling, setToggling] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [extending, setExtending] = useState(false)
  const [roster, setRoster] = useState<RosterMember[]>(() => buildRosterFromKeys(MINIMAL_ROSTER_KEYS))
  const [extendDate, setExtendDate] = useState<string>(() => {
    if (org.expiresAt) {
      return org.expiresAt.split('T')[0]
    }
    const thirtyDays = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    return thirtyDays.toISOString().split('T')[0]
  })
  const [seedResult, setSeedResult] = useState<{
    credentials: { email: string; password: string }[]
    copiedRows: Set<string>
    dismissed: boolean
  } | null>(null)
  const [regenResult, setRegenResult] = useState<{
    credentials: { email: string; password: string }[]
    copiedRows: Set<string>
    dismissed: boolean
  } | null>(null)

  const resolvedPersonas = (): 'all' | string[] => {
    const emails = Array.from(new Set(roster.map((member) => PERSONA_BY_KEY[member.personaKey].email)))
    return emails
  }

  const applyMinimalPreset = () => setRoster(buildRosterFromKeys(MINIMAL_ROSTER_KEYS))
  const applyFullPreset = () => setRoster(buildRosterFromKeys(PERSONA_OPTIONS.map((option) => option.key)))

  const usedPersonaKeys = new Set(roster.map((member) => member.personaKey))
  const firstAvailablePersona = (): PersonaKey | null => {
    const available = PERSONA_OPTIONS.find((option) => !usedPersonaKeys.has(option.key))
    return available ? available.key : null
  }

  const addRosterMember = () => {
    const fallback = firstAvailablePersona() ?? PERSONA_OPTIONS[0].key
    const option = PERSONA_BY_KEY[fallback]
    setRoster((prev) => [
      ...prev,
      { id: nextRosterId(), name: option.defaultName, email: option.email, personaKey: fallback },
    ])
  }

  const removeRosterMember = (id: string) => {
    setRoster((prev) => prev.filter((member) => member.id !== id))
  }

  const updateRosterMember = (id: string, patch: Partial<Omit<RosterMember, 'id'>>) => {
    setRoster((prev) => prev.map((member) => {
      if (member.id !== id) return member
      const next = { ...member, ...patch }
      if (patch.personaKey && patch.personaKey !== member.personaKey) {
        const option = PERSONA_BY_KEY[patch.personaKey]
        if (member.email === PERSONA_BY_KEY[member.personaKey].email) next.email = option.email
        if (member.name === PERSONA_BY_KEY[member.personaKey].defaultName) next.name = option.defaultName
      }
      return next
    }))
  }

  const handleToggleDemo = useCallback(async (checked: boolean) => {
    setToggling(true)
    try {
      await adminApi.patchOrg(orgId, { demoTenant: checked })
      queryClient.invalidateQueries({ queryKey: ['admin-org', orgId] })
      queryClient.invalidateQueries({ queryKey: ['admin-orgs'] })
      toast.success(checked ? 'Demo flag enabled' : 'Demo flag disabled')
    } catch {
      toast.error('Failed to update demo flag')
    } finally {
      setToggling(false)
    }
  }, [orgId, queryClient])

  const handleSeedDemo = useCallback(async () => {
    setSeeding(true)
    setSeedResult(null)
    try {
      const result = await adminApi.seedDemoOrg(orgId, { personas: resolvedPersonas() })
      setSeedResult({ credentials: result.oneTimeCredentials, copiedRows: new Set(), dismissed: false })
      queryClient.invalidateQueries({ queryKey: ['admin-org', orgId] })
      toast.success('Demo data seeded')
    } catch {
      toast.error('Failed to seed demo data')
    } finally {
      setSeeding(false)
    }
  }, [orgId, queryClient, roster])

  const handleRegenCredentials = useCallback(async () => {
    setRegenerating(true)
    setRegenResult(null)
    try {
      const result = await demoTenantsApi.regenerateCredentials(orgId)
      setRegenResult({ credentials: result.oneTimeCredentials, copiedRows: new Set(), dismissed: false })
      toast.success('Credentials regenerated')
    } catch {
      toast.error('Failed to regenerate credentials')
    } finally {
      setRegenerating(false)
    }
  }, [orgId])

  const handleExtendByDays = (days: number) => {
    const base = org.expiresAt ? new Date(org.expiresAt) : new Date()
    base.setDate(base.getDate() + days)
    setExtendDate(base.toISOString().split('T')[0])
  }

  const handleExtendSave = useCallback(async () => {
    if (!extendDate) return
    setExtending(true)
    try {
      await adminApi.extendDemo(orgId, { until: new Date(extendDate).toISOString() })
      queryClient.invalidateQueries({ queryKey: ['admin-org', orgId] })
      queryClient.invalidateQueries({ queryKey: ['admin-orgs'] })
      toast.success('Expiration extended')
    } catch {
      toast.error('Failed to extend demo expiration')
    } finally {
      setExtending(false)
    }
  }, [orgId, extendDate, queryClient])

  return (
    <div className="py-4 space-y-6">
      <div className="flex items-center justify-between rounded-lg border border-border p-4">
        <div>
          <p className="text-sm font-medium text-foreground">Demo tenant</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Mark this organization as a demo for prospect walkthroughs.
          </p>
        </div>
        <Button
          variant={org.demoTenant ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleToggleDemo(!org.demoTenant)}
          disabled={toggling}
          aria-pressed={org.demoTenant}
          aria-label="Toggle demo tenant"
        >
          {toggling
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            : org.demoTenant ? 'Demo: On' : 'Demo: Off'
          }
        </Button>
      </div>

      {org.demoTenant && (
        <div className="space-y-6">
          <div className="rounded-lg border border-border bg-card p-4 space-y-3">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <p className="text-sm font-semibold text-foreground">Expiration</p>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Access count
                </p>
                <p className="text-foreground">{org.accessCount}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Last accessed
                </p>
                <p className="text-foreground">
                  {org.lastAccessedAt
                    ? new Date(org.lastAccessedAt).toLocaleDateString()
                    : 'Never'
                  }
                </p>
              </div>
              {org.expiresAt && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Current expiry
                  </p>
                  <p className="text-foreground">
                    {new Date(org.expiresAt).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="extend-date">Extend expiration to</Label>
              <Input
                id="extend-date"
                type="date"
                value={extendDate}
                onChange={(e) => setExtendDate(e.target.value)}
                aria-label="Expiration date"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleExtendByDays(30)}
                  aria-label="Add 30 days"
                >
                  +30 days
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleExtendByDays(60)}
                  aria-label="Add 60 days"
                >
                  +60 days
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleExtendByDays(90)}
                  aria-label="Add 90 days"
                >
                  +90 days
                </Button>
              </div>
              <Button
                type="button"
                size="sm"
                onClick={handleExtendSave}
                disabled={extending || !extendDate}
                aria-label="Save expiration date"
              >
                {extending
                  ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" aria-hidden="true" />Extending…</>
                  : <>Save expiration</>
                }
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4 space-y-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <p className="text-sm font-semibold text-foreground">Members</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={applyMinimalPreset}
                  aria-label="Use minimal preset"
                >
                  Minimal
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={applyFullPreset}
                  aria-label="Use full preset"
                >
                  Full
                </Button>
              </div>
            </div>
            {roster.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                No members yet. Add a member or apply a preset.
              </p>
            ) : (
              <div className="space-y-2">
                {roster.map((member) => (
                  <div
                    key={member.id}
                    className="grid grid-cols-12 gap-2 items-start"
                  >
                    <div className="col-span-3">
                      <Input
                        value={member.name}
                        onChange={(event) => updateRosterMember(member.id, { name: event.target.value })}
                        placeholder="Name"
                        aria-label={`Member ${member.id} name`}
                      />
                    </div>
                    <div className="col-span-4">
                      <Input
                        value={member.email}
                        onChange={(event) => updateRosterMember(member.id, { email: event.target.value })}
                        placeholder="Email"
                        type="email"
                        aria-label={`Member ${member.id} email`}
                      />
                    </div>
                    <div className="col-span-4">
                      <select
                        value={member.personaKey}
                        onChange={(event) => updateRosterMember(member.id, { personaKey: event.target.value as PersonaKey })}
                        aria-label={`Member ${member.id} persona`}
                        className={cn(
                          'flex h-10 w-full rounded-md border border-input bg-background',
                          'px-3 py-2 text-sm ring-offset-background',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        )}
                      >
                        {PERSONA_OPTIONS.map((option) => (
                          <option key={option.key} value={option.key}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeRosterMember(member.id)}
                        aria-label={`Remove ${member.name}`}
                      >
                        <UserMinus className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addRosterMember}
              aria-label="Add member"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
              Add member
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={handleSeedDemo}
              disabled={seeding}
              aria-label="Seed demo data"
            >
              {seeding
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />Seeding…</>
                : <><RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />Seed demo data</>
              }
            </Button>
            <Button
              variant="outline"
              onClick={handleRegenCredentials}
              disabled={regenerating}
              aria-label="Regenerate persona passwords"
            >
              {regenerating
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />Regenerating…</>
                : <><KeyRound className="h-4 w-4 mr-2" aria-hidden="true" />Regenerate persona passwords</>
              }
            </Button>
          </div>
        </div>
      )}

      {seedResult && seedResult.credentials.length > 0 && !seedResult.dismissed && (
        <CredentialsBanner
          credentials={seedResult.credentials}
          copiedRows={seedResult.copiedRows}
          onCopyRow={(email) => setSeedResult((prev) => prev
            ? { ...prev, copiedRows: new Set([...prev.copiedRows, email]) }
            : prev
          )}
          onDismiss={() => setSeedResult((prev) => prev ? { ...prev, dismissed: true } : null)}
        />
      )}

      {regenResult && regenResult.credentials.length > 0 && !regenResult.dismissed && (
        <CredentialsBanner
          credentials={regenResult.credentials}
          copiedRows={regenResult.copiedRows}
          onCopyRow={(email) => setRegenResult((prev) => prev
            ? { ...prev, copiedRows: new Set([...prev.copiedRows, email]) }
            : prev
          )}
          onDismiss={() => setRegenResult((prev) => prev ? { ...prev, dismissed: true } : null)}
        />
      )}

      {isPlatformAdmin && (
        <ResetExchangeDemoSection orgId={orgId} />
      )}
    </div>
  )
}

interface DangerTabProps {
  org: AdminOrgFull
  orgId: string
}

const DangerTab: React.FC<DangerTabProps> = ({ org, orgId }) => {
  const navigate = useNavigate()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const {
    register,
    watch,
    reset: resetForm,
  } = useForm<DeleteConfirmFormValues>({
    resolver: zodResolver(deleteConfirmSchema.refine(
      (data) => data.confirmation === org.name,
      { path: ['confirmation'], message: `Type "${org.name}" to confirm` }
    )),
    defaultValues: { confirmation: '' },
    mode: 'onChange',
  })

  const confirmValue = watch('confirmation')
  const confirmationMatches = confirmValue === org.name

  const handleDelete = useCallback(async () => {
    setDeleting(true)
    try {
      await adminApi.deleteOrg(orgId)
      navigate('/app/admin/orgs')
      toast.success(`"${org.name}" has been deleted`)
    } catch {
      toast.error('Failed to delete organization')
      setDeleting(false)
    }
  }, [orgId, org.name, navigate])

  return (
    <div className="py-4 space-y-6">
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-3">
        <div className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <p className="text-sm font-semibold">Danger zone</p>
        </div>
        <p className="text-sm text-muted-foreground">
          Soft-deleting this organization will archive it and remove all active member access.
          This action cannot be undone without contacting an engineer.
        </p>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => setConfirmOpen(true)}
        >
          <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />
          Delete organization
        </Button>
      </div>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) resetForm()
          setConfirmOpen(open)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <DialogHeaderWithIcon
              icon={Trash2}
              iconColor="rose"
              title={`Delete "${org.name}"?`}
              description="This will archive the organization and revoke all member access. Type the organization name to confirm."
              variant="alertDialog"
            />
          </AlertDialogHeader>
          <div className="px-1 pb-2 space-y-1.5">
            <Label htmlFor="delete-confirm">
              Type <span className="font-mono font-bold">{org.name}</span> to confirm
            </Label>
            <Input
              id="delete-confirm"
              placeholder={org.name}
              aria-label="Type organization name to confirm deletion"
              {...register('confirmation')}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                resetForm()
                setConfirmOpen(false)
              }}
              disabled={deleting}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
              disabled={!confirmationMatches || deleting}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

const TABS = [
  { value: 'branding', label: 'Branding', icon: Palette },
  { value: 'org-info', label: 'Org Info', icon: ClipboardList },
  { value: 'members', label: 'Members', icon: Users },
  { value: 'accounts', label: 'Accounts', icon: Building2 },
  { value: 'demo', label: 'Demo / Seed', icon: Sparkles },
  { value: 'danger', label: 'Danger', icon: AlertTriangle },
] as const

type TabValue = typeof TABS[number]['value']

export const AdminOrgEditor: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>()
  const [activeTab, setActiveTab] = useState<TabValue>('branding')
  const [brandingDirty, setBrandingDirty] = useState(false)
  const [orgInfoDirty, setOrgInfoDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const queryClient = useQueryClient()

  const brandingSubmitRef = useRef<(() => Promise<void>) | null>(null)
  const orgInfoSubmitRef = useRef<(() => Promise<void>) | null>(null)

  const { data: org, isLoading, isError } = useQuery({
    queryKey: ['admin-org', orgId],
    queryFn: () => adminApi.getOrgById(orgId!),
    enabled: !!orgId,
    staleTime: 60_000,
  })

  const dirty = brandingDirty || orgInfoDirty

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      if (activeTab === 'branding' && brandingSubmitRef.current) {
        await brandingSubmitRef.current()
      } else if (activeTab === 'org-info' && orgInfoSubmitRef.current) {
        await orgInfoSubmitRef.current()
      } else {
        toast.success('Changes saved')
      }
    } catch {
      toast.error('Failed to save changes')
    } finally {
      setSaving(false)
      setBrandingDirty(false)
      setOrgInfoDirty(false)
    }
  }, [activeTab])

  const handleDiscard = useCallback(() => {
    setBrandingDirty(false)
    setOrgInfoDirty(false)
    queryClient.invalidateQueries({ queryKey: ['admin-org', orgId] })
  }, [orgId, queryClient])

  const handleDeleteLogo = useCallback(async () => {
    await brandingApi.deleteLogo(orgId!)
    queryClient.invalidateQueries({ queryKey: ['admin-org', orgId] })
    queryClient.invalidateQueries({ queryKey: ['admin-orgs'] })
    queryClient.invalidateQueries({ queryKey: ['org-branding', orgId] })
    toast.success('Logo deleted')
  }, [orgId, queryClient])

  const handleDeleteLogoDark = useCallback(async () => {
    await brandingApi.deleteLogoDark(orgId!)
    queryClient.invalidateQueries({ queryKey: ['admin-org', orgId] })
    queryClient.invalidateQueries({ queryKey: ['admin-orgs'] })
    queryClient.invalidateQueries({ queryKey: ['org-branding', orgId] })
    toast.success('Dark logo deleted')
  }, [orgId, queryClient])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
      </div>
    )
  }

  if (isError || !org) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-3">
        <AlertTriangle className="h-8 w-8 text-destructive" aria-hidden="true" />
        <p role="alert" className="text-sm text-destructive font-medium">
          Failed to load organization.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => queryClient.invalidateQueries({ queryKey: ['admin-org', orgId] })}
        >
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <OrgNameBar
        orgName={org.name}
        saving={saving}
        dirty={dirty}
        onSave={handleSave}
        onDiscard={handleDiscard}
      />

      <div className="max-w-5xl mx-auto px-6 py-6">
        <TabsPrimitive.Root
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as TabValue)}
        >
          <TabsPrimitive.List
            className="flex border-b border-border gap-0"
            aria-label="Organization editor sections"
          >
            {TABS.map((tab) => {
              const IconComponent = tab.icon
              return (
                <TabsPrimitive.Trigger
                  key={tab.value}
                  value={tab.value}
                  className={cn(
                    'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-muted-foreground',
                    'border-b-2 border-transparent transition-colors',
                    'data-[state=active]:border-primary data-[state=active]:text-foreground',
                    tab.value === 'danger' && 'data-[state=active]:border-destructive data-[state=active]:text-destructive',
                  )}
                >
                  <IconComponent className="h-3.5 w-3.5" aria-hidden="true" />
                  {tab.label}
                </TabsPrimitive.Trigger>
              )
            })}
          </TabsPrimitive.List>

          <TabsPrimitive.Content value="branding">
            <BrandingTab
              org={org}
              orgId={orgId!}
              onDirtyChange={setBrandingDirty}
              submitRef={brandingSubmitRef}
              onDeleteLogo={handleDeleteLogo}
              onDeleteLogoDark={handleDeleteLogoDark}
            />
          </TabsPrimitive.Content>

          <TabsPrimitive.Content value="org-info">
            <OrgInfoTab
              org={org}
              orgId={orgId!}
              onDirtyChange={setOrgInfoDirty}
              submitRef={orgInfoSubmitRef}
            />
          </TabsPrimitive.Content>

          <TabsPrimitive.Content value="members">
            <MembersTab orgId={orgId!} />
          </TabsPrimitive.Content>

          <TabsPrimitive.Content value="accounts">
            <AccountsTab orgId={orgId!} />
          </TabsPrimitive.Content>

          <TabsPrimitive.Content value="demo">
            <DemoSeedTab
              org={org}
              orgId={orgId!}
            />
          </TabsPrimitive.Content>

          <TabsPrimitive.Content value="danger">
            <DangerTab
              org={org}
              orgId={orgId!}
            />
          </TabsPrimitive.Content>
        </TabsPrimitive.Root>
      </div>
    </div>
  )
}
